import React, { useState, useRef } from 'react'
import { Upload, X, Edit2, Save, Image as ImageIcon, Tag } from 'lucide-react'
import { API_BASE_URL } from '../../config/api'

interface ImageData {
  id: string
  url: string
  file?: File
  label: string
  description?: string
  isNew?: boolean
}

interface ImageUploadProps {
  images: ImageData[]
  onImagesChange: (images: ImageData[]) => void
  maxImages?: number
  acceptedTypes?: string[]
  accommodationId?: string
  tenantId?: string
  onImageDelete?: (imageId: string) => Promise<boolean>
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onImagesChange,
  maxImages = 10,
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  accommodationId,
  tenantId,
  onImageDelete
}) => {
  const [dragOver, setDragOver] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const generateId = () => Math.random().toString(36).substr(2, 9)

  // Helper function to construct image URLs consistently
  const getImageUrl = (imageUrl: string) => {
    if (!imageUrl) return ''
    // Handle URLs that already have the correct format
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl
    }
    // Handle absolute paths starting with /uploads/
    if (imageUrl.startsWith('/uploads/')) {
      return `${API_BASE_URL}${imageUrl}`
    }
    // Handle relative paths - add full prefix
    const path = imageUrl.startsWith('uploads/') ? imageUrl : `uploads/images/${imageUrl}`
    return `${API_BASE_URL}/${path}`
  }

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return

    const newImages: ImageData[] = []
    const remainingSlots = maxImages - images.length

    Array.from(files).slice(0, remainingSlots).forEach((file) => {
      if (acceptedTypes.includes(file.type)) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const newImage: ImageData = {
            id: generateId(),
            url: e.target?.result as string,
            file,
            label: '',
            description: '',
            isNew: true
          }
          newImages.push(newImage)
          if (newImages.length === Math.min(files.length, remainingSlots)) {
            onImagesChange([...images, ...newImages])
          }
        }
        reader.readAsDataURL(file)
      }
    })
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const removeImage = async (id: string) => {
    const image = images.find(img => img.id === id)
    if (!image) return

    // If it's a new image (not saved to backend yet), just remove from state
    if (image.isNew) {
      onImagesChange(images.filter(img => img.id !== id))
      return
    }

    // If it's an existing image, try to delete from backend first
    if (onImageDelete && accommodationId && tenantId) {
      const confirmed = window.confirm('Are you sure you want to delete this image?')
      if (!confirmed) return

      try {
        const success = await onImageDelete(id)
        if (success) {
          onImagesChange(images.filter(img => img.id !== id))
        } else {
          alert('Failed to delete image from server')
        }
      } catch (error) {
        console.error('Error deleting image:', error)
        alert('Failed to delete image')
      }
    } else {
      // Fallback: just remove from state
      onImagesChange(images.filter(img => img.id !== id))
    }
  }

  const startEditing = (image: ImageData) => {
    setEditingId(image.id)
    setEditLabel(image.label)
    setEditDescription(image.description || '')
  }

  const saveEdit = () => {
    if (!editingId) return
    
    const updatedImages = images.map(img => 
      img.id === editingId 
        ? { ...img, label: editLabel, description: editDescription }
        : img
    )
    onImagesChange(updatedImages)
    setEditingId(null)
    setEditLabel('')
    setEditDescription('')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditLabel('')
    setEditDescription('')
  }

  const suggestedLabels = [
    'Exterior', 'Lobby', 'Room', 'Bathroom', 'Restaurant', 'Pool', 'Spa', 'Gym', 
    'Conference Room', 'Bar', 'Garden', 'Balcony', 'View', 'Amenities'
  ]

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver 
              ? 'border-purple-400 bg-purple-50' 
              : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            Drop images here or click to upload
          </p>
          <p className="text-sm text-gray-600">
            PNG, JPG, WEBP up to 10MB each. Max {maxImages} images.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {images.length} of {maxImages} images uploaded
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(',')}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image) => (
            <div key={image.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Image */}
              <div className="relative h-48">
                <img
                  src={getImageUrl(image.url)}
                  alt={image.label || 'Hotel image'}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeImage(image.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                {image.isNew && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                    New
                  </div>
                )}
              </div>

              {/* Label and Description */}
              <div className="p-4">
                {editingId === image.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Label *
                      </label>
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        placeholder="e.g., Exterior, Lobby, Room"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                    </div>
                    
                    {/* Suggested Labels */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quick Labels
                      </label>
                      <div className="flex flex-wrap gap-1">
                        {suggestedLabels.map((label) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setEditLabel(label)}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-purple-100 text-gray-700 rounded-lg transition-colors"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Optional description..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={!editLabel.trim()}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-1 transition-colors"
                      >
                        <Save className="w-3 h-3" />
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-3 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {image.label || 'Unlabeled'}
                        </span>
                      </div>
                      <button
                        onClick={() => startEditing(image)}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                    
                    {image.description && (
                      <p className="text-sm text-gray-600">
                        {image.description}
                      </p>
                    )}
                    
                    {!image.label && (
                      <p className="text-xs text-red-500">
                        Please add a label for this image
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Images State */}
      {images.length === 0 && (
        <div className="text-center py-8">
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No images uploaded yet</p>
        </div>
      )}
    </div>
  )
}

export default ImageUpload